import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, IndianRupee, Package, Clock } from "lucide-react";
import { formatGeneratedAt } from "../lib/datetime";

function convictionClass(score) {
  if (score >= 80) return "conviction-high text-bm-green";
  if (score >= 65) return "conviction-mid text-bm-amber";
  return "conviction-low text-bm-danger";
}

const UX_COLORS = {
  Whitespace: "border-bm-cyan/50 text-bm-cyan",
  Emerging: "border-bm-amber/50 text-bm-amber",
  Validated: "border-bm-green/50 text-bm-green",
  Saturated: "border-bm-danger/50 text-bm-danger",
};

function buildStructuredTitle(opp) {
  const parts = [opp.category, opp.subcategory, opp.signal_type].filter(Boolean);
  return parts.join(" · ");
}

export default function OpportunityCard({ opp, idx = 0 }) {
  const uxCls = UX_COLORS[opp.ux_label] || "border-white/20 text-bm-muted";
  const structured = buildStructuredTitle(opp);
  const nomenclature = opp.product_nomenclature || opp.product_name;
  const generatedLabel = formatGeneratedAt(opp.generated_at || opp.created_at);

  return (
    <Link
      to={`/opportunities/${opp.id}`}
      data-testid={`opportunity-card-${opp.id}`}
      className="bm-card fade-up p-5 flex flex-col gap-4 group"
      style={{ animationDelay: `${Math.min(idx * 50, 500)}ms` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {opp.ux_label && (
            <span className={`label-mono px-1.5 py-0.5 border ${uxCls}`} data-testid={`ux-label-${opp.id}`}>
              {opp.ux_label.toUpperCase()}
            </span>
          )}
          {opp.source_type === "insight_promotion" && (
            <span className="label-mono px-1.5 py-0.5 border border-bm-cyan/50 text-bm-cyan" data-testid={`insight-grounded-${opp.id}`}>
              INSIGHT-GROUNDED
            </span>
          )}
          <span className="label-mono text-bm-dim">FROM / {opp.origin_geography}</span>
        </div>
        <div className={`px-2 py-1 border font-mono text-xs flex items-center gap-2 ${convictionClass(opp.scores.overall)}`} data-testid={`conviction-${opp.id}`}>
          {opp.scores.overall}% CONVICTION
          {opp.retrain_delta && opp.retrain_delta.delta !== 0 && (
            <span
              className={`flex items-center gap-0.5 pl-2 border-l border-white/10 ${opp.retrain_delta.delta > 0 ? "text-bm-green" : "text-bm-danger"}`}
              title={`Model retrained from launch: ${opp.retrain_delta.launch_name}`}
              data-testid={`retrain-delta-${opp.id}`}
            >
              {opp.retrain_delta.delta > 0 ? "▲" : "▼"}{Math.abs(opp.retrain_delta.delta)}
            </span>
          )}
        </div>
      </div>

      <div>
        {/* Structured taxonomy line */}
        <div className="label-mono text-bm-cyan mb-1.5 truncate" data-testid={`opp-structured-${opp.id}`}>
          {structured}
        </div>
        <h3 className="font-heading text-xl font-bold leading-tight tracking-tight text-white group-hover:text-bm-amber transition-colors">
          {nomenclature}
        </h3>
        {opp.tagline && <p className="text-sm text-bm-muted mt-1">{opp.tagline}</p>}
      </div>

      <p className="text-sm leading-relaxed text-bm-text/85 line-clamp-3">{opp.why_now}</p>

      {/* Score bars */}
      <div className="grid grid-cols-5 gap-2 pt-2 border-t border-white/5">
        {[
          ["CAT", opp.scores.category],
          ["BEH", opp.scores.behaviour],
          ["PRD", opp.scores.product],
          ["LOC", opp.scores.localization],
          ["COM", opp.scores.commercial],
        ].map(([k, v]) => (
          <div key={k} className="text-center">
            <div className="label-mono text-bm-dim">{k}</div>
            <div className={`font-mono text-sm mt-0.5 ${v >= 80 ? "text-bm-green" : v >= 65 ? "text-bm-amber" : "text-bm-danger"}`}>{v}</div>
            <div className="h-0.5 bg-white/5 mt-1">
              <div className={`h-full ${v >= 80 ? "bg-bm-green" : v >= 65 ? "bg-bm-amber" : "bg-bm-danger"}`} style={{ width: `${v}%` }}></div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between text-xs font-mono pt-2">
        <div className="flex items-center gap-3 text-bm-muted">
          <span className="flex items-center gap-1"><IndianRupee className="w-3 h-3" />{opp.price_inr}</span>
          <span className="flex items-center gap-1"><Package className="w-3 h-3" />{opp.pack_size}</span>
        </div>
        <ArrowUpRight className="w-4 h-4 text-bm-dim group-hover:text-bm-amber transition-colors" />
      </div>

      {/* Generated-at stamp */}
      <div className="flex items-center gap-1 text-[10px] font-mono text-bm-dim border-t border-white/5 pt-2" data-testid={`opp-generated-at-${opp.id}`}>
        <Clock className="w-3 h-3" />
        <span>Generated {generatedLabel}</span>
      </div>
    </Link>
  );
}
